import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Repeat, ChevronLeft, ChevronRight, Plus, Pencil, Trash2, Flame, Trophy, Check } from "lucide-react";
import { usePrefs, useFormat } from "../contexts/PrefsContext";

const TODAY_ISO = (() => new Date().toISOString().slice(0, 10))();
const monthLabel = (iso) => {
    const [y, m] = iso.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
};
const ymd = (y, m, d) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const shiftMonth = (iso, delta) => {
    const [y, m] = iso.split("-").map(Number);
    const dt = new Date(y, m - 1 + delta, 1);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
};

export default function HabitTracker() {
    const { prefs } = usePrefs();
    const { locale } = useFormat();
    const weekStart = prefs.week_start ?? 1;  // 0=Sun .. 6=Sat (ISO-ish)
    const [month, setMonth] = useState(() => TODAY_ISO.slice(0, 7)); // "YYYY-MM"
    const [grid, setGrid] = useState(null);
    const [busy, setBusy] = useState(false);
    const [name, setName] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editVal, setEditVal] = useState("");
    // Visual celebration per cell after tick
    const [pulse, setPulse] = useState({}); // key `${habitId}-${date}` -> true

    const load = async (m = month) => {
        const { data } = await api.get(`/habits/grid?month=${m}`);
        setGrid(data);
    };
    useEffect(() => { load(month); }, [month]);

    const days = useMemo(() => {
        if (!grid) return [];
        const [y, m] = grid.month.split("-").map(Number);
        const last = new Date(y, m, 0).getDate();
        // Localised weekday narrow label (e.g. "M","T","W"…)
        const fmtDow = new Intl.DateTimeFormat(locale || "en-GB", { weekday: "narrow" });
        return Array.from({ length: last }, (_, i) => i + 1).map((d) => {
            const dt = new Date(y, m - 1, d);
            const dow = dt.getDay(); // 0=Sun..6=Sat
            return {
                day: d,
                iso: ymd(y, m, d),
                isToday: ymd(y, m, d) === TODAY_ISO,
                isFuture: ymd(y, m, d) > TODAY_ISO,
                dow,
                dowLabel: fmtDow.format(dt),
                isWeekStart: dow === weekStart,
            };
        });
    }, [grid, locale, weekStart]);

    const addHabit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        await api.post("/habits", { name: name.trim(), streak: 0, log: [] });
        setName("");
        await load();
    };

    const toggleCell = async (habit, iso) => {
        if (busy || iso > TODAY_ISO) return;
        setBusy(true);
        const key = `${habit.id}-${iso}`;
        // Optimistic update + pulse
        setGrid((g) => {
            if (!g) return g;
            return {
                ...g,
                rows: g.rows.map((r) => {
                    if (r.habit.id !== habit.id) return r;
                    const has = r.month_days.includes(iso);
                    const next = has ? r.month_days.filter((d) => d !== iso) : [...r.month_days, iso].sort();
                    const completed_today_new = iso === TODAY_ISO ? !has : r.completed_today;
                    return { ...r, month_days: next, month_completed: next.length, completed_today: completed_today_new };
                }),
                habits_completed_today: g.habits_completed_today + (
                    iso === TODAY_ISO ? (g.rows.find(r => r.habit.id === habit.id)?.completed_today ? -1 : 1) : 0
                ),
            };
        });
        setPulse((p) => ({ ...p, [key]: true }));
        setTimeout(() => setPulse((p) => { const n = { ...p }; delete n[key]; return n; }), 700);
        try {
            const { data } = await api.post(`/habits/${habit.id}/toggle`, { date: iso });
            setGrid((g) => g && {
                ...g,
                rows: g.rows.map((r) => r.habit.id === habit.id
                    ? { ...r, current_streak: data.current_streak, best_streak: data.best_streak }
                    : r),
            });
        } catch {
            await load();
        } finally {
            setBusy(false);
        }
    };

    const renameHabit = async (habit) => {
        const v = editVal.trim();
        setEditingId(null);
        if (v && v !== habit.name) {
            await api.put(`/habits/${habit.id}`, { name: v });
            await load();
        }
    };
    const deleteHabit = async (habit) => {
        if (!window.confirm(`Delete habit "${habit.name}"? This removes all of its history.`)) return;
        await api.delete(`/habits/${habit.id}`);
        await load();
    };

    if (!grid) return <div className="card-warm p-6 text-sm text-[var(--text-ink)]/60" data-testid="habits-loading">Loading habit tracker…</div>;

    const totalHabits = grid.habits_total;
    const doneToday = grid.habits_completed_today;
    const todayPct = totalHabits ? Math.round((doneToday / totalHabits) * 100) : 0;

    return (
        <div data-testid="habit-tracker">
            {/* Header */}
            <div className="card-warm p-4 sm:p-5 mb-4">
                <div className="flex items-center gap-2 sm:gap-3">
                    <button onClick={() => setMonth(shiftMonth(month, -1))}
                            data-testid="habits-prev-month"
                            aria-label="Previous month"
                            className="btn-ghost text-sm inline-flex items-center gap-1 px-2 sm:px-3"><ChevronLeft size={14}/><span className="hidden sm:inline">Prev</span></button>
                    <div className="font-serif text-xl sm:text-2xl flex-1 text-center truncate" data-testid="habits-month-label">{monthLabel(grid.month)}</div>
                    <button onClick={() => setMonth(shiftMonth(month, 1))}
                            disabled={shiftMonth(month, 1) > TODAY_ISO.slice(0, 7)}
                            data-testid="habits-next-month"
                            aria-label="Next month"
                            className="btn-ghost text-sm inline-flex items-center gap-1 px-2 sm:px-3 disabled:opacity-40"><span className="hidden sm:inline">Next</span><ChevronRight size={14}/></button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 text-sm">
                    <SummaryStat icon={Repeat} label="Habits tracked" value={totalHabits} testid="hab-stat-total" />
                    <SummaryStat icon={Check} label="Completed today" value={`${doneToday} / ${totalHabits}`} testid="hab-stat-today" highlight={doneToday === totalHabits && totalHabits > 0} />
                    <SummaryStat icon={Flame} label="Hottest streak" value={`${Math.max(0, ...grid.rows.map(r => r.current_streak))}d`} testid="hab-stat-current" />
                    <SummaryStat icon={Trophy} label="All-time best" value={`${Math.max(0, ...grid.rows.map(r => r.best_streak))}d`} testid="hab-stat-best" />
                </div>
                {totalHabits > 0 && (
                    <div className="mt-3 h-1.5 bg-[rgba(26,19,24,0.06)] rounded-full overflow-hidden" data-testid="hab-today-bar">
                        <div className="h-full brand-gradient transition-[width] duration-500" style={{ width: `${todayPct}%` }} />
                    </div>
                )}
            </div>

            {/* Add habit form */}
            <form onSubmit={addHabit} className="card-warm p-3 sm:p-4 flex gap-2 mb-3" data-testid="habit-add-form">
                <Repeat size={18} className="self-center text-[var(--brand-purple)] shrink-0" />
                <input value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="New habit (e.g. read 20 min)"
                       data-testid="habit-input"
                       className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                <button className="btn-primary inline-flex items-center gap-1 shrink-0" data-testid="habit-add-btn">
                    <Plus size={14} /> Add
                </button>
            </form>

            {/* Grid */}
            {totalHabits === 0 ? (
                <div className="card-warm p-10 text-center text-[var(--text-ink)]/60" data-testid="habits-empty">
                    Add your first habit above to start tracking.
                </div>
            ) : (
                <>
                    {/* Mobile: card-per-habit with month strip that wraps by week */}
                    <div className="md:hidden space-y-3" data-testid="habits-cards">
                        {grid.rows.map((r) => (
                            <HabitCard
                                key={r.habit.id}
                                row={r}
                                days={days}
                                editingId={editingId}
                                editVal={editVal}
                                setEditVal={setEditVal}
                                setEditingId={setEditingId}
                                renameHabit={renameHabit}
                                deleteHabit={deleteHabit}
                                toggleCell={toggleCell}
                                pulse={pulse}
                                weekStart={weekStart}
                            />
                        ))}
                    </div>

                    {/* Desktop: full month table */}
                    <div className="hidden md:block card-warm p-2 sm:p-3 overflow-x-auto scrollbar-thin" data-testid="habits-grid-scroll">
                        <table className="w-full text-sm border-separate" style={{ borderSpacing: 0 }}>
                            <thead>
                                <tr>
                                    <th className="sticky left-0 z-20 bg-[var(--bg-cream)] text-left py-2 pl-2 pr-3 min-w-[10rem] font-medium text-[var(--text-ink)]/70">
                                        Habit
                                    </th>
                                    {days.map((d) => (
                                        <th key={d.iso}
                                            className={`px-1 py-2 text-center text-[10px] font-medium tabular-nums ${d.isToday ? "text-[var(--brand-orange)] font-bold" : "text-[var(--text-ink)]/50"} ${d.isWeekStart ? "border-l border-[var(--brand-purple)]/25" : ""}`}>
                                            <div className="leading-none">{d.day}</div>
                                            <div className="leading-none text-[9px] mt-0.5 text-[var(--text-ink)]/40">{d.dowLabel}</div>
                                        </th>
                                    ))}
                                    <th className="sticky right-0 z-20 bg-[var(--bg-cream)] px-3 text-[10px] font-medium text-[var(--text-ink)]/70 text-right whitespace-nowrap">
                                        Streak / Best
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {grid.rows.map((r) => {
                                    const set = new Set(r.month_days);
                                    const pct = r.month_total_days ? Math.round((r.month_completed / r.month_total_days) * 100) : 0;
                                    return (
                                        <tr key={r.habit.id} data-testid={`habit-row-${r.habit.id}`} className="group">
                                            <td className="sticky left-0 z-10 bg-[var(--bg-cream)] py-2 pl-2 pr-3 border-t border-[rgba(26,19,24,0.05)]">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    {editingId === r.habit.id ? (
                                                        <input autoFocus value={editVal}
                                                               onChange={(e) => setEditVal(e.target.value)}
                                                               onBlur={() => renameHabit(r.habit)}
                                                               onKeyDown={(e) => { if (e.key === "Enter") renameHabit(r.habit); if (e.key === "Escape") setEditingId(null); }}
                                                               data-testid={`habit-rename-input-${r.habit.id}`}
                                                               className="px-2 py-1 rounded-lg border border-[var(--brand-purple)] bg-white/70 flex-1 min-w-0" />
                                                    ) : (
                                                        <button onClick={() => { setEditingId(r.habit.id); setEditVal(r.habit.name); }}
                                                                data-testid={`habit-name-${r.habit.id}`}
                                                                className="font-medium truncate text-left hover:text-[var(--brand-purple)] transition flex-1 min-w-0">
                                                            {r.habit.name}
                                                        </button>
                                                    )}
                                                    <button onClick={() => { setEditingId(r.habit.id); setEditVal(r.habit.name); }}
                                                            data-testid={`habit-edit-${r.habit.id}`}
                                                            className="text-[var(--text-ink)]/40 hover:text-[var(--brand-purple)] opacity-0 group-hover:opacity-100 transition">
                                                        <Pencil size={12} />
                                                    </button>
                                                    <button onClick={() => deleteHabit(r.habit)}
                                                            data-testid={`habit-delete-${r.habit.id}`}
                                                            className="text-[var(--text-ink)]/40 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition">
                                                        <Trash2 size={12} />
                                                    </button>
                                                </div>
                                                <div className="text-[10px] text-[var(--text-ink)]/55 mt-0.5" data-testid={`habit-summary-${r.habit.id}`}>
                                                    {r.month_completed}/{r.month_total_days} days · {pct}% consistency
                                                </div>
                                            </td>
                                            {days.map((d) => {
                                                const checked = set.has(d.iso);
                                                const key = `${r.habit.id}-${d.iso}`;
                                                const pulsing = !!pulse[key];
                                                return (
                                                    <td key={d.iso}
                                                        className={`px-0.5 py-1 text-center border-t border-[rgba(26,19,24,0.04)] ${d.isWeekStart ? "border-l border-[var(--brand-purple)]/20" : ""}`}>
                                                        <button onClick={() => toggleCell(r.habit, d.iso)}
                                                                disabled={d.isFuture}
                                                                data-testid={`habit-cell-${r.habit.id}-${d.day}`}
                                                                aria-label={`${r.habit.name} ${d.iso} ${checked ? "completed" : "not completed"}`}
                                                                className={`vu-habit-cell ${checked ? "is-on" : ""} ${d.isToday ? "is-today" : ""} ${d.isFuture ? "is-future" : ""} ${pulsing ? "is-pulse" : ""}`}>
                                                            {checked && <Check size={11} strokeWidth={3} />}
                                                        </button>
                                                    </td>
                                                );
                                            })}
                                            <td className="sticky right-0 z-10 bg-[var(--bg-cream)] pl-3 pr-2 text-right whitespace-nowrap border-t border-[rgba(26,19,24,0.05)]">
                                                <div className="inline-flex items-center gap-1 text-xs">
                                                    <Flame size={12} className={r.current_streak >= 3 ? "text-[var(--brand-orange)]" : "text-[var(--text-ink)]/40"} />
                                                    <span className="font-medium tabular-nums" data-testid={`habit-current-${r.habit.id}`}>{r.current_streak}</span>
                                                    <span className="text-[var(--text-ink)]/45">/</span>
                                                    <Trophy size={12} className="text-[var(--brand-purple)]" />
                                                    <span className="font-medium tabular-nums" data-testid={`habit-best-${r.habit.id}`}>{r.best_streak}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <style>{`
                .vu-habit-cell {
                    width: 22px; height: 22px; border-radius: 6px;
                    display: inline-flex; align-items: center; justify-content: center;
                    border: 1.5px solid rgba(26,19,24,0.18);
                    background: rgba(255,255,255,0.5);
                    color: var(--text-ink);
                    cursor: pointer;
                    transition: transform .15s ease, background .25s ease, border-color .25s ease, box-shadow .25s ease;
                }
                .vu-habit-cell:hover:not(:disabled) {
                    border-color: var(--brand-orange);
                    transform: translateY(-1px);
                }
                .vu-habit-cell.is-on {
                    background: linear-gradient(135deg, var(--brand-purple), var(--brand-magenta), var(--brand-orange));
                    border-color: transparent;
                    color: white;
                    box-shadow: 0 1px 4px rgba(123,45,142,0.30);
                }
                .vu-habit-cell.is-today {
                    box-shadow: 0 0 0 2px rgba(255,107,53,0.35);
                }
                .vu-habit-cell.is-future {
                    cursor: not-allowed;
                    opacity: 0.35;
                    background: transparent;
                }
                .vu-habit-cell.is-pulse {
                    animation: vu-habit-tick .55s ease-out;
                }
                /* Mobile cards use a larger tap target (36px) — safe for finger taps
                 * while still fitting a 7-day week strip inside a 320px card. */
                .vu-habit-cell.is-mobile {
                    width: 34px; height: 34px; border-radius: 9px;
                }
                @keyframes vu-habit-tick {
                    0%   { transform: scale(0.9); }
                    35%  { transform: scale(1.45); box-shadow: 0 0 0 6px rgba(233,30,99,0.18); }
                    70%  { transform: scale(0.95); box-shadow: 0 0 0 12px rgba(233,30,99,0); }
                    100% { transform: scale(1); }
                }
                @media (prefers-reduced-motion: reduce) {
                    .vu-habit-cell.is-pulse { animation: none; }
                }
            `}</style>
        </div>
    );
}

/**
 * Mobile card view — one card per habit.
 * Shows the current week's 7 days as a horizontal strip inside the card;
 * additional weeks appear below (wrapping) so users can scroll vertically
 * to see the whole month rather than horizontally.
 */
function HabitCard({ row, days, editingId, editVal, setEditVal, setEditingId,
                     renameHabit, deleteHabit, toggleCell, pulse, weekStart }) {
    const set = new Set(row.month_days);
    const pct = row.month_total_days ? Math.round((row.month_completed / row.month_total_days) * 100) : 0;

    // Group month days into rows-of-7 that start on the user's preferred
    // week start. The first row is left-padded to align its columns with
    // the fixed weekday labels above; subsequent rows are naturally aligned.
    const dowIndex = (dow) => (dow - weekStart + 7) % 7;
    const weeks = useMemo(() => {
        const out = [];
        let current = new Array(dowIndex(days[0]?.dow ?? weekStart)).fill(null);
        for (const d of days) {
            current.push(d);
            if (current.length === 7) { out.push(current); current = []; }
        }
        if (current.length) out.push([...current, ...new Array(7 - current.length).fill(null)]);
        return out;
    }, [days, weekStart]);

    // Weekday labels (M T W …) aligned with the week starts on user's pref.
    const weekdayLabels = useMemo(() => {
        if (!days.length) return [];
        const fmt = new Intl.DateTimeFormat(undefined, { weekday: "narrow" });
        const anchor = new Date(2024, 0, 7); // Sunday
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(anchor);
            d.setDate(anchor.getDate() + ((weekStart + i) % 7));
            return fmt.format(d);
        });
    }, [days, weekStart]);

    return (
        <div className="card-warm p-3" data-testid={`habit-card-${row.habit.id}`}>
            {/* Row 1: name + streaks + actions */}
            <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                    {editingId === row.habit.id ? (
                        <input autoFocus value={editVal}
                               onChange={(e) => setEditVal(e.target.value)}
                               onBlur={() => renameHabit(row.habit)}
                               onKeyDown={(e) => { if (e.key === "Enter") renameHabit(row.habit); if (e.key === "Escape") setEditingId(null); }}
                               data-testid={`habit-mobile-rename-${row.habit.id}`}
                               className="w-full px-2 py-1 rounded-lg border border-[var(--brand-purple)] bg-white/70 text-base" />
                    ) : (
                        <button onClick={() => { setEditingId(row.habit.id); setEditVal(row.habit.name); }}
                                data-testid={`habit-mobile-name-${row.habit.id}`}
                                className="font-medium text-base leading-tight text-left hover:text-[var(--brand-purple)] transition break-words w-full">
                            {row.habit.name}
                        </button>
                    )}
                    <div className="text-[11px] text-[var(--text-ink)]/60 mt-0.5"
                         data-testid={`habit-mobile-summary-${row.habit.id}`}>
                        {row.month_completed}/{row.month_total_days} days · {pct}% consistency
                    </div>
                </div>

                {/* Streak / Best — compact chip */}
                <div className="shrink-0 flex flex-col items-end gap-0.5 text-[11px]">
                    <div className="inline-flex items-center gap-1 tabular-nums">
                        <Flame size={12} className={row.current_streak >= 3 ? "text-[var(--brand-orange)]" : "text-[var(--text-ink)]/40"} />
                        <span className="font-medium" data-testid={`habit-mobile-current-${row.habit.id}`}>{row.current_streak}d</span>
                    </div>
                    <div className="inline-flex items-center gap-1 tabular-nums">
                        <Trophy size={12} className="text-[var(--brand-purple)]" />
                        <span className="font-medium" data-testid={`habit-mobile-best-${row.habit.id}`}>{row.best_streak}d</span>
                    </div>
                </div>

                {/* Edit / Delete — always visible on mobile (no hover) */}
                <div className="shrink-0 flex flex-col gap-1 -mr-1 -mt-1">
                    <button onClick={() => { setEditingId(row.habit.id); setEditVal(row.habit.name); }}
                            data-testid={`habit-mobile-edit-${row.habit.id}`}
                            aria-label="Rename habit"
                            className="w-8 h-8 grid place-items-center rounded-lg text-[var(--text-ink)]/50 hover:text-[var(--brand-purple)] active:bg-[rgba(26,19,24,0.04)]">
                        <Pencil size={14} />
                    </button>
                    <button onClick={() => deleteHabit(row.habit)}
                            data-testid={`habit-mobile-delete-${row.habit.id}`}
                            aria-label="Delete habit"
                            className="w-8 h-8 grid place-items-center rounded-lg text-[var(--text-ink)]/50 hover:text-rose-600 active:bg-[rgba(255,0,0,0.04)]">
                        <Trash2 size={14} />
                    </button>
                </div>
            </div>

            {/* Row 2: weekday header + month grid (wraps by week) */}
            <div className="mt-3">
                <div className="grid grid-cols-7 gap-1 mb-1">
                    {weekdayLabels.map((lbl, i) => (
                        <div key={i} className="text-center text-[10px] uppercase tracking-wider text-[var(--text-ink)]/40">
                            {lbl}
                        </div>
                    ))}
                </div>
                <div className="space-y-1">
                    {weeks.map((week, wi) => (
                        <div key={wi} className="grid grid-cols-7 gap-1">
                            {week.map((d, di) => {
                                if (!d) return <div key={di} />;
                                const checked = set.has(d.iso);
                                const key = `${row.habit.id}-${d.iso}`;
                                const pulsing = !!pulse[key];
                                return (
                                    <div key={d.iso} className="flex justify-center">
                                        <button onClick={() => toggleCell(row.habit, d.iso)}
                                                disabled={d.isFuture}
                                                data-testid={`habit-mobile-cell-${row.habit.id}-${d.day}`}
                                                aria-label={`${row.habit.name} ${d.iso} ${checked ? "completed" : "not completed"}`}
                                                className={`vu-habit-cell is-mobile relative ${checked ? "is-on" : ""} ${d.isToday ? "is-today" : ""} ${d.isFuture ? "is-future" : ""} ${pulsing ? "is-pulse" : ""}`}>
                                            {checked
                                                ? <Check size={14} strokeWidth={3} />
                                                : <span className="text-[10px] text-[var(--text-ink)]/45 tabular-nums">{d.day}</span>}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function SummaryStat({ icon: Icon, label, value, testid, highlight }) {
    return (
        <div data-testid={testid} className={`rounded-xl px-3 py-2 inline-flex items-center gap-2 ${highlight ? "brand-gradient text-white" : "bg-[rgba(26,19,24,0.04)]"}`}>
            <Icon size={16} className={highlight ? "text-white" : "text-[var(--brand-purple)]"} />
            <div className="leading-tight min-w-0">
                <div className={`text-[9px] uppercase tracking-wider ${highlight ? "text-white/85" : "text-[var(--text-ink)]/55"}`}>{label}</div>
                <div className="font-serif text-lg leading-none">{value}</div>
            </div>
        </div>
    );
}
